import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Maximize2, X } from 'lucide-react';

// Fix for default markers in react-leaflet
// eslint-disable-next-line @typescript-eslint/no-explicit-any
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Poland's approximate center coordinates
const POLAND_CENTER: [number, number] = [52.0693, 19.4803];
const POLAND_BOUNDS: [[number, number], [number, number]] = [
  [49.0, 14.0], // Southwest corner
  [55.0, 25.0]  // Northeast corner
];

interface PolandMapProps {
  latitude?: string;
  longitude?: string;
  onLocationSelect: (lat: string, lng: string) => void;
  height?: string;
}

// Component to change map view when location changes
const ChangeMapView: React.FC<{ center: [number, number], zoom: number, shouldZoom: boolean }> = ({ center, zoom, shouldZoom }) => {
  const map = useMap();
  const lastCenterRef = React.useRef<[number, number] | null>(null);
  
  useEffect(() => {
    // Tylko wykonaj zoom jeśli powinien być wykonany i pozycja się zmieniła
    if (shouldZoom && center) {
      const [lat, lng] = center;
      const lastCenter = lastCenterRef.current;
      
      // Sprawdź czy pozycja faktycznie się zmieniła (z tolerancją)
      if (!lastCenter || 
          Math.abs(lastCenter[0] - lat) > 0.0001 || 
          Math.abs(lastCenter[1] - lng) > 0.0001) {
        map.setView(center, zoom, { animate: true, duration: 0.5 });
        lastCenterRef.current = center;
      }
    }
  }, [center, zoom, shouldZoom, map]);
  return null;
};

const LocationMarker: React.FC<{ 
  onLocationSelect: (lat: string, lng: string) => void;
  initialPosition?: [number, number] | null;
  onUserClick?: () => void;
}> = ({ onLocationSelect, initialPosition, onUserClick }) => {
  const [position, setPosition] = useState<[number, number] | null>(initialPosition || null);
  const lastInitialPositionRef = React.useRef<string | null>(null);
  const isUserInteractionRef = React.useRef(false);

  // Aktualizuj pozycję gdy zmieni się initialPosition (tylko jeśli faktycznie się zmieniła i nie jest to klik użytkownika)
  useEffect(() => {
    if (initialPosition && !isUserInteractionRef.current) {
      const posKey = `${initialPosition[0]}-${initialPosition[1]}`;
      // Aktualizuj tylko jeśli pozycja faktycznie się zmieniła
      if (lastInitialPositionRef.current !== posKey) {
        setPosition(initialPosition);
        lastInitialPositionRef.current = posKey;
      }
    } else if (!initialPosition && position) {
      // Jeśli initialPosition jest null, ale mamy pozycję, zostaw ją (użytkownik może kliknąć)
    }
  }, [initialPosition, position]);

  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      const newPosition: [number, number] = [lat, lng];
      
      // Oznacz że to interakcja użytkownika
      isUserInteractionRef.current = true;
      
      // Zaktualizuj pozycję natychmiast
      setPosition(newPosition);
      
      // Powiadom rodzica że to klik użytkownika
      if (onUserClick) {
        onUserClick();
      }
      
      // Wywołaj callback z opóźnieniem żeby uniknąć blokowania
      requestAnimationFrame(() => {
        onLocationSelect(lat.toFixed(6), lng.toFixed(6));
        
        // Reset flagi po aktualizacji
        setTimeout(() => {
          isUserInteractionRef.current = false;
        }, 200);
      });
    },
  });

  return position === null ? null : (
    <Marker position={position} key={`marker-${position[0]}-${position[1]}`}>
      <Popup>
        <div className="text-center">
          <div className="font-semibold mb-1">Wybrana lokalizacja</div>
          <div className="text-sm text-gray-600">
            Szerokość: {position[0].toFixed(6)}<br />
            Długość: {position[1].toFixed(6)}
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

const PolandMap: React.FC<PolandMapProps> = ({
  latitude,
  longitude,
  onLocationSelect,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  height = '400px'
}) => {
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldZoom, setShouldZoom] = useState(false);
  const lastPositionRef = React.useRef<string | null>(null);
  const isUserClickRef = React.useRef(false);

  useEffect(() => {
    // Set position from props if available
    if (latitude && longitude && !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude))) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      const posKey = `${lat}-${lng}`;
      
      // Pomiń aktualizację jeśli pozycja się nie zmieniła
      if (lastPositionRef.current === posKey) {
        return;
      }
      
      // Jeśli to klik użytkownika, tylko zaktualizuj pozycję bez zoomu
      if (isUserClickRef.current) {
        setCurrentPosition([lat, lng]);
        lastPositionRef.current = posKey;
        setShouldZoom(false);
        // Reset flagi po aktualizacji (z opóźnieniem żeby uniknąć race condition)
        setTimeout(() => {
          isUserClickRef.current = false;
        }, 300);
        return;
      }
      
      // Normalna aktualizacja z props - wykonaj zoom
      setCurrentPosition([lat, lng]);
      setShouldZoom(true);
      lastPositionRef.current = posKey;
      
      // Reset shouldZoom po krótkim czasie
      setTimeout(() => {
        setShouldZoom(false);
      }, 1000);
    }
  }, [latitude, longitude]);

  // Handler dla kliknięć użytkownika - wyłączamy automatyczny zoom
  const handleLocationSelect = React.useCallback((lat: string, lng: string) => {
    isUserClickRef.current = true; // Oznacz że to klik użytkownika
    setShouldZoom(false); // Wyłącz automatyczny zoom dla kliknięć użytkownika
    // Użyj requestAnimationFrame żeby uniknąć blokowania
    requestAnimationFrame(() => {
      onLocationSelect(lat, lng);
    });
  }, [onLocationSelect]);

  // Handler dla kliknięć użytkownika na mapie
  const handleUserClick = React.useCallback(() => {
    isUserClickRef.current = true;
    setShouldZoom(false);
  }, []);

  const mapHeight = '350px';

  return (
    <div className="space-y-4">
      <div className="bg-[var(--bg-card)] p-4 rounded-lg border border-[var(--border-primary)]">
        {/* Coordinates input fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Szerokość geograficzna
            </label>
            <input
              type="text"
              value={latitude || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= -90 && parseFloat(value) <= 90)) {
                  onLocationSelect(value, longitude || '');
                }
              }}
              placeholder="np. 52.2297"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Długość geograficzna
            </label>
            <input
              type="text"
              value={longitude || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= -180 && parseFloat(value) <= 180)) {
                  onLocationSelect(latitude || '', value);
                }
              }}
              placeholder="np. 21.0122"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-primary)] rounded-lg overflow-hidden relative">
        {/* Przycisk powiększenia - prawy dolny róg */}
        <button
          onClick={() => setIsModalOpen(true)}
          className="absolute bottom-4 right-4 z-[1000] bg-[var(--bg-card)] hover:bg-blue-50 border border-[var(--border-primary)] rounded-lg p-3 shadow-lg transition-all hover:shadow-xl"
          title="Powiększ mapę"
        >
          <Maximize2 className="w-5 h-5 text-gray-700" />
        </button>

        <MapContainer
          center={currentPosition || POLAND_CENTER}
          zoom={currentPosition ? 15 : 6}
          style={{ height: mapHeight, width: '100%' }}
          maxBounds={POLAND_BOUNDS}
          maxBoundsViscosity={1.0}
        >
          {currentPosition && (
            <ChangeMapView center={currentPosition} zoom={15} shouldZoom={shouldZoom} />
          )}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Click handler for new location selection - jedna pinezka która się aktualizuje */}
          <LocationMarker 
            onLocationSelect={handleLocationSelect} 
            initialPosition={currentPosition}
            onUserClick={handleUserClick}
          />
        </MapContainer>
      </div>

      {/* Modal z pełną mapą */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="relative w-full h-full max-w-7xl max-h-[90vh] bg-[var(--bg-card)] rounded-lg shadow-2xl overflow-hidden">
            {/* Przycisk zamknięcia */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 z-[10000] bg-[var(--bg-card)] hover:bg-red-50 border border-[var(--border-primary)] rounded-md p-2 shadow-md transition-colors"
              title="Zamknij"
            >
              <X className="w-5 h-5 text-gray-700 hover:text-red-600" />
            </button>

            {/* Pełna mapa w modalu */}
            <MapContainer
              center={currentPosition || POLAND_CENTER}
              zoom={currentPosition ? 15 : 6}
              style={{ height: '100%', width: '100%' }}
              maxBounds={POLAND_BOUNDS}
              maxBoundsViscosity={1.0}
            >
              {currentPosition && (
                <ChangeMapView center={currentPosition} zoom={15} shouldZoom={shouldZoom} />
              )}
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {/* Click handler for new location selection - jedna pinezka która się aktualizuje */}
              <LocationMarker 
                onLocationSelect={handleLocationSelect} 
                initialPosition={currentPosition}
                onUserClick={handleUserClick}
              />
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolandMap;
